import type React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {generatePath, useNavigate, useParams} from "react-router";
import {Button, Skeleton} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import {routes} from "../../routing/routes";
import {useStores} from "../../stores";
import type {DashboardItem, DashboardSource} from "./services/dashboardsApi";
import {useHermesDashboard, useInvalidateMyDashboards, useMyDashboards} from "./services/dashboardsQueries";
import {SupersetEmbed} from "./SupersetEmbed";
import {AtlasDashboardPanel} from "./AtlasDashboardPanel";
import {dashboardSubtitle} from "./dashboards.utils";
import "./dashboards.scss";

export type DashboardDetailRouteMatch = {id: string; source: string; dashboardId: string};

function useDashboard(source: DashboardSource | null, dashboardId: string, bagId: number) {
    const hermes = useHermesDashboard(dashboardId, source === "hermes");
    const mine = useMyDashboards(source === "mine" ? bagId : 0);
    if (source === "hermes") {
        return {dashboard: hermes.data ?? null, isLoading: hermes.isPending, error: hermes.error};
    }
    if (source === "mine") {
        const dashboard = mine.data?.dashboards.find(d => d.id === dashboardId) ?? null;
        return {dashboard, isLoading: Boolean(bagId) && mine.isPending, error: mine.error};
    }
    return {dashboard: null, isLoading: false, error: null};
}

export const DashboardDetailPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams<DashboardDetailRouteMatch>();
    const {bagStore} = useStores();
    const bagId = bagStore.bagId;
    const source: DashboardSource | null = params.source === "hermes" || params.source === "mine" ? params.source : null;
    const dashboardId = params.dashboardId ?? "";
    const {dashboard, isLoading, error} = useDashboard(source, dashboardId, bagId);

    const canvasRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    useEffect(() => {
        const onChange = () => setIsFullscreen(document.fullscreenElement === canvasRef.current);
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);
    const toggleFullscreen = () => {
        if (document.fullscreenElement) {
            void document.exitFullscreen();
        } else {
            void canvasRef.current?.requestFullscreen?.();
        }
    };

    const back = () => navigate(generatePath(routes.dashboards, {id: String(bagId)}));
    // "Edit with Atlas" docks the DashGen chat next to the dashboard; every change reloads the embed.
    const [atlasOpen, setAtlasOpen] = useState(false);
    const [embedVersion, setEmbedVersion] = useState(0);
    const invalidateMine = useInvalidateMyDashboards();
    const editWithAtlas = () => {
        if (dashboard?.sessionId) setAtlasOpen(true);
    };
    const onDashboardChanged = useCallback(() => {
        setEmbedVersion(v => v + 1);
        void invalidateMine(bagId);
    }, [bagId, invalidateMine]);
    useEffect(() => setAtlasOpen(false), [dashboardId]);

    const editable = dashboard?.source === "mine";
    const subtitle = dashboard ? dashboardSubtitle(dashboard) : "";

    return (
        <div className={atlasOpen ? "dsh-shell dsh-shell--atlas" : "dsh-shell"}>
        <div className="dsh-shell-main">
        <div className="dsh-root dsh-detail">
            <div className="dsh-top">
                <button type="button" className="dsh-back" onClick={back}>
                    <ArrowBackIcon sx={{fontSize: 16}}/> Dashboards
                </button>
                {isLoading ? (
                    <div className="dsh-dhead">
                        <div className="dsh-dhead-l">
                            <Skeleton variant="text" width={280} height={36}/>
                            <Skeleton variant="text" width={360}/>
                        </div>
                    </div>
                ) : dashboard ? (
                    <div className="dsh-dhead">
                        <div className="dsh-dhead-l">
                            <div className="dsh-titlerow">
                                <h1 className="dsh-t-page-title">{dashboard.title}</h1>
                                {editable ? (
                                    <span className="dsh-tag">Yours</span>
                                ) : (
                                    <span className="dsh-tag dsh-tag--lock">
                                        <LockOutlinedIcon sx={{fontSize: 14}}/> Built-in · view only
                                    </span>
                                )}
                            </div>
                            {subtitle && <p className="dsh-t-subtitle dsh-sub">{subtitle}</p>}
                        </div>
                        <div className="dsh-headact">
                            <Button
                                variant="outlined"
                                className="dsh-btn-secondary"
                                startIcon={isFullscreen ? <FullscreenExitIcon/> : <FullscreenIcon/>}
                                onClick={toggleFullscreen}
                                disabled={!dashboard.embeddedUuid || !dashboard.supersetDomain}
                            >
                                {isFullscreen ? "Exit full screen" : "Full screen"}
                            </Button>
                            {editable && (
                                <Button
                                    variant="contained"
                                    className="dsh-btn-ai"
                                    startIcon={<AutoAwesomeIcon/>}
                                    onClick={editWithAtlas}
                                    disabled={!dashboard.sessionId || atlasOpen}
                                >
                                    Edit with Atlas
                                </Button>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            <div className="dsh-canvas" ref={canvasRef}>
                {isLoading ? (
                    <Skeleton variant="rounded" height="100%" sx={{borderRadius: "12px"}}/>
                ) : error ? (
                    <div className="dsh-empty dsh-empty--canvas" role="alert">
                        <p className="dsh-t-card-title">This dashboard could not be loaded</p>
                        <p className="dsh-t-body-sm">{error.message}</p>
                    </div>
                ) : !dashboard ? (
                    <div className="dsh-empty dsh-empty--canvas">
                        <DashboardOutlinedIcon sx={{fontSize: 28}}/>
                        <p className="dsh-t-card-title">Dashboard not found</p>
                        <p className="dsh-t-body-sm">It may have been removed or unpublished.</p>
                        <Button size="small" variant="outlined" onClick={back}>Back to dashboards</Button>
                    </div>
                ) : !dashboard.embeddedUuid || !dashboard.supersetDomain ? (
                    <div className="dsh-empty dsh-empty--canvas">
                        <DashboardOutlinedIcon sx={{fontSize: 28}}/>
                        <p className="dsh-t-card-title">This dashboard can't be shown here yet</p>
                        <p className="dsh-t-body-sm">
                            {editable
                                ? "Open it in Atlas to view and edit it."
                                : "It has not been set up for embedding."}
                        </p>
                        {editable && dashboard.sessionId && (
                            <Button size="small" variant="outlined" onClick={editWithAtlas}>Open in Atlas</Button>
                        )}
                    </div>
                ) : (
                    <SupersetEmbed
                        key={`${dashboard.embeddedUuid}@${dashboard.supersetDomain}#${embedVersion}`}
                        embeddedUuid={dashboard.embeddedUuid}
                        supersetDomain={dashboard.supersetDomain}
                    />
                )}
            </div>
        </div>
        </div>
            {atlasOpen && editable && dashboard?.sessionId && (
                <AtlasDashboardPanel
                    mode={{kind: "edit", sessionId: dashboard.sessionId}}
                    onClose={() => setAtlasOpen(false)}
                    onDashboardChanged={onDashboardChanged}
                />
            )}
        </div>
    );
});
